import { getApiBase } from '../api/http';

const getToken = () => {
  try { return window.localStorage.getItem('token') || ''; } catch (e) { return ''; }
};

const isAbsoluteUrl = (u) => /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(u) || u.startsWith('//');

const isPresignedMinioUrl = (u) => /X-Amz-Algorithm=AWS4-HMAC-SHA256/i.test(String(u || ''));

// MinIO/S3 直链常见的“响应控制”参数（非签名也可能出现，例如后端 presign 失败的 fallback URL）
const isS3LikeResponseParamUrl = (u) => {
  const s = String(u || '').trim();
  if (!s || !isAbsoluteUrl(s)) return false;
  try {
    const url = new URL(s);
    return url.searchParams.has('response-content-disposition') || url.searchParams.has('response-content-type');
  } catch (e) {
    return false;
  }
};

// 从 MinIO/S3 URL 推导 objectName（key），用于转为后端 /api/minio/object 代理访问。
// 支持：
// - presigned URL（带 X-Amz-Algorithm）
// - fallback 直链（带 response-content-disposition/response-content-type）
const extractObjectNameFromS3LikeUrl = (rawUrl) => {
  const s = String(rawUrl || '').trim();
  if (!s || !isAbsoluteUrl(s)) return '';
  if (!isPresignedMinioUrl(s) && !isS3LikeResponseParamUrl(s)) return '';
  try {
    const u = new URL(s);
    const segments = String(u.pathname || '').split('/').filter(Boolean);
    // path-style: /<bucket>/<object...>
    if (segments.length >= 2) {
      return segments
        .slice(1)
        .map((seg) => {
          try { return decodeURIComponent(seg); } catch (e) { return seg; }
        })
        .join('/');
    }
    // virtual-hosted style: https://<bucket>.<host>/<object...>
    if (segments.length >= 1) {
      return segments
        .map((seg) => {
          try { return decodeURIComponent(seg); } catch (e) { return seg; }
        })
        .join('/');
    }
  } catch (e) {
    // ignore
  }
  return '';
};

// 将后端返回的相对 URL（/api/... 或 /hrms/api/...）补全为“登录页设置的后端地址”。
// 这样在本地开发时，即使未启动本机后端，也不会走 CRA proxy -> localhost:3004。
const resolveApiUrl = (rawUrl) => {
  const u = String(rawUrl || '').trim();
  if (!u) return '';
  if (u.startsWith('blob:') || u.startsWith('data:') || isAbsoluteUrl(u)) return u;

  const base = String(getApiBase() || '').trim().replace(/\/+$/g, '');
  if (!base) return u;

  const isApiPath =
    u === '/api' ||
    u.startsWith('/api/') ||
    u === '/hrms/api' ||
    u.startsWith('/hrms/api/') ||
    // 资源类接口经常以 /behavior/... 返回（用于 axios baseURL 拼接），但 fetch/AuthedImage 需要补全到同一后端。
    u.startsWith('/behavior/');
  if (!isApiPath) return u;

  const baseTrim = base.replace(/\/+$/g, '');
  if (u.startsWith('/behavior/')) {
    // base 已包含 /api 或 /hrms/api；直接拼接 /behavior/... 即可
    return `${baseTrim}${u}`;
  }
  const rest = u.startsWith('/hrms/api') ? u.slice('/hrms/api'.length) : u.slice('/api'.length);
  return `${baseTrim}${rest}`;
};

export const buildMinioProxyUrl = (objectName, opts = {}) => {
  const raw = String(objectName || '').trim();
  if (!raw) return '';

  // 已经是后端代理地址：直接补全为绝对 URL
  if (raw === '/api/minio/object' || raw.startsWith('/api/minio/object?') || raw === '/hrms/api/minio/object' || raw.startsWith('/hrms/api/minio/object?')) {
    return resolveApiUrl(raw);
  }

  let name = raw;
  // 如果传入的是 S3/MinIO URL（presigned 或 fallback），则解析其中的 objectName 后再转为代理 URL
  if (isAbsoluteUrl(raw)) {
    const extracted = extractObjectNameFromS3LikeUrl(raw);
    if (!extracted) return '';
    name = extracted;
  }

  const base = String(getApiBase() || '').replace(/\/+$/g, '');
  const url = new URL(base + '/minio/object', window.location.origin);
  url.searchParams.set('objectName', name);
  if (opts.download) url.searchParams.set('download', '1');
  if (opts.filename) url.searchParams.set('filename', String(opts.filename));
  return url.toString();
};

export async function fetchBlob(url) {
  let u = resolveApiUrl(url);
  if (!u) throw new Error('missing_url');
  // 如果是 MinIO/S3 URL（presigned 或 fallback），优先转成后端代理 URL（避免证书/CORS/权限/路径异常导致失败）
  if (isPresignedMinioUrl(u) || isS3LikeResponseParamUrl(u)) {
    const proxied = buildMinioProxyUrl(u);
    if (proxied) u = proxied;
  }
  const token = getToken();
  const resp = await fetch(u, {
    method: 'GET',
    headers: token ? { Authorization: 'Bearer ' + token } : {},
    credentials: 'include'
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    const err = new Error(text || ('http_' + resp.status));
    err.status = resp.status;
    throw err;
  }
  return await resp.blob();
}

export async function fetchBlobUrl(url) {
  const blob = await fetchBlob(url);
  const objectUrl = URL.createObjectURL(blob);
  return objectUrl;
}

export async function openWithAuth(url) {
  let u = resolveApiUrl(url);
  if (!u) return;
  // blob/data：本地预览无需额外请求
  if (u.startsWith('blob:') || u.startsWith('data:')) {
    window.open(u, '_blank', 'noopener,noreferrer');
    return;
  }
  // MinIO/S3 URL（presigned 或 fallback）：尽量转为后端代理再打开（更稳，避免直连 MinIO）
  if (isPresignedMinioUrl(u) || isS3LikeResponseParamUrl(u)) {
    const proxied = buildMinioProxyUrl(u);
    if (proxied) u = proxied;
  }
  const objUrl = await fetchBlobUrl(u);
  window.open(objUrl, '_blank', 'noopener,noreferrer');
  // 交给浏览器新窗口加载后再释放会更稳，这里延迟回收
  setTimeout(() => {
    try { URL.revokeObjectURL(objUrl); } catch (e) {}
  }, 60_000);
}

export async function downloadWithAuth(url, filename) {
  let u = resolveApiUrl(url);
  if (!u) return;
  if (u.startsWith('blob:') || u.startsWith('data:')) {
    const a = document.createElement('a');
    a.href = u;
    a.download = filename || 'download';
    a.rel = 'noopener noreferrer';
    a.click();
    return;
  }
  // MinIO/S3（presigned 或 fallback）：尽量转为后端代理再下载
  if (isPresignedMinioUrl(u) || isS3LikeResponseParamUrl(u)) {
    const proxied = buildMinioProxyUrl(u, filename ? { filename } : {});
    if (proxied) u = proxied;
  }

  const blob = await fetchBlob(u);
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objUrl;
  a.download = filename || 'download';
  a.rel = 'noopener noreferrer';
  a.click();
  setTimeout(() => {
    try { URL.revokeObjectURL(objUrl); } catch (e) {}
  }, 60_000);
}
